import type { FileMetadata } from '~/utilities/pyscript-api-models';

export type TreeNode = FileNodeType | FolderNodeType;

export interface FileNodeType {
  children: TreeNode[];
  label: string;
  type: 'file';
  metadata: FileMetadata;
}

export interface FolderNodeType {
  children: TreeNode[];
  label: string;
  type: 'folder';
  dirname: string;
}

export interface Output {
  tree: TreeNode[];
  cache: Record<string, Output>;
}

/**
 * Builds a file tree object by utilizing the path of each file.
 */
export function buildFileTree(fileMetadata: FileMetadata[]): TreeNode[] {
  const output: Output = { tree: [], cache: {} };

  // Sort the paths alphabetically
  const fileMetadataSorted = Array.from(fileMetadata).sort((a, b) => a.path.localeCompare(b.path));

  fileMetadataSorted.forEach((metadata) => {
    const path = metadata.path;
    /**
     * If the path ends with a /, the last array item will be an empty string.
     * The filter removes these falsy values.
     */
    const splitPath = path.split('/').filter(Boolean);
    const endsWithSlash = path.at(-1) === '/';

    splitPath.reduce((r: Output, label, index) => {
      if (!r.cache[label]) {
        // Create a cache of the files and folders added to our tree
        r.cache[label] = {
          tree: [],
          cache: {},
        };
        const fileName = splitPath.at(-1);

        const isFile = label === fileName && index === splitPath.length - 1 && !endsWithSlash;

        // Add to the tree
        r.tree.push({
          label,
          children: r.cache[label].tree,
          ...(isFile
            ? { metadata, type: 'file' }
            : {
                type: 'folder',
                dirname: splitPath.slice(0, index + 1).join('/'),
              }),
        });
      }

      return r.cache[label];
    }, output);
  });

  const folders = output.tree.filter((item: TreeNode) => item.type === 'folder');
  const files = output.tree.filter((item) => item.type === 'file');

  return [...folders, ...files];
}
